import { Box, Button, Typography, Divider } from "@mui/material";
//import { Box } from "@mui/system";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useCallback, useEffect, useMemo, useState } from "react";
import AlerteDialog from "../../AlerteDialog/AlerteDialog";
import { ITurnover, ITurnoverFormValues } from "../../../interface/turnover";
import TurnoverInfosFields from "./TurnoverInfosFields";
import TurnoverMonthFields from "./TurnoverMonthFields";
import getBusinessDaysForMonth from "../../../tools/getBusinessDays";
import axios from "axios";
import { urlAPI } from "../../../config/config";
import TurnoverFixedFields from "./TurnoverFixedFields";

const validationSchema = yup.object({
  agency: yup.string().trim(),
  collab: yup.string(),
  project: yup.string(),
  client: yup.string().required("Le client est obligatoire"),
  type: yup.string().required("Le type de prestation est obligatoire"),
});


const AddUpdateTurnover = ({
  turnoverId,
  handleSubmit,
  forecastId,
  version,
  setOpenModal,
  monthList,
  monthlyAmount
}: {
  turnoverId?: string;
  handleSubmit: Function;
  forecastId?: string,
  version?: number,
  setOpenModal: Function,
  monthList: { name: string, field: string }[],
  monthlyAmount: number,
}) => {
  let dispatch = useDispatch();
  let token = useSelector((state: RootState) => state.user.token) as string;

  const [turnover, setTurnover] = useState<ITurnover>();
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [tjm, setTjm] = useState<number>(0);
  const [error, setError] = useState("");

  const getTurnover = useCallback(async () => {
    if (!token || !turnoverId) return;
    try {
      const syncResponse = await axios.get(`${urlAPI}/forecast/turnover/${turnoverId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTurnover(syncResponse.data.message);
    } catch (error: any) {
      setError("Erreur lors de la récupération des données.");
    }
  }, [token, turnoverId]);

  useEffect(() => {
    getTurnover();
  }, [getTurnover]);

  // LOGIQUE : Calcul des valeurs initiales incluant les jours ouvrés par défaut
  const initialValues = useMemo(() => {
    const defaultMonths = monthList.map((item: any) => {
        const [monthName, yearStr] = item.field.split(/(\d+)/).filter(Boolean);
        return {
          label: item.field,
          openDays: getBusinessDaysForMonth(Number(yearStr), monthName.trim()),
          tjm: 0,
          amount: 0,
        };
    });

    return {
      agency: turnover?.agency || "Toutes",
      collab: turnover?.collab?._id || "",
      fictifCollab: turnover?.fictifCollab || "",
      project: turnover?.project || "",
      client: turnover?.client?._id || "",
      type: turnover?.type || "",
      monthlyAmount: turnover?.monthlyAmount || 0,
      months: turnover?.months || defaultMonths,
    };
  }, [turnover, monthList]);
  
  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: (values: ITurnoverFormValues) => {
      handleSubmit(token, values, forecastId, version!, setOpenModal, dispatch, setError, turnoverId);
    },
    enableReinitialize: true, // Crucial pour mettre à jour le formulaire quand 'turnover' arrive de l'API
  });

  const handleClose = useCallback(() => setShowConfirmDialog(true), []);
  const confirmClose = useCallback(() => { setShowConfirmDialog(false); setOpenModal(false); }, [setOpenModal]);
  const cancelClose = useCallback(() => setShowConfirmDialog(false), []);

  return (
    <Box 
      component="form" 
      onSubmit={formik.handleSubmit} 
      sx={{ 
        display: "flex", 
        flexDirection: "column",
        gap: 3, 
        p: 3, 
        bgcolor: "#fcfcfc", 
        borderRadius: 3,
        boxShadow: "0px 8px 24px rgba(0,0,0,0.08)",
        width: "100%"
      }}
    >

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 4 }}>
        {/* Colonne GAUCHE : Configuration (plus étroite, focus sur les sélections) */}
        <Box sx={{ flex: "1 1 400px" }}> {/* flex-grow: 1, flex-shrink: 1, basis: 400px */}
            <TurnoverInfosFields 
              turnover={turnover} 
              formik={formik} 
              setTjm={setTjm} 
            />
        </Box>

        {/* Colonne DROITE : Saisie mensuelle */}
        <Box sx={{ 
          flex: "2 1 600px", 
          p: 3, 
          bgcolor: "white", 
          borderRadius: 2, 
          border: "1px solid #edf2f7" 
        }}>
            <TurnoverMonthFields 
              formik={formik} 
              monthList={monthList} 
              tjm={tjm} 
            />
        </Box>
      </Box>

      <Divider sx={{ mt: 2 }} />

      {/* Barre d'actions alignée à droite pour la validation */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          color="inherit"
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Annuler
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          size="large" 
          sx={{ 
            borderRadius: 2, 
            px: 6, 
            textTransform: 'none',
            fontWeight: 'bold',
            boxShadow: '0px 4px 12px rgba(25, 118, 210, 0.3)' 
          }}
        >
          {turnoverId ? "Mettre à jour" : "Créer la prestation"}
        </Button>
      </Box>

      {/* Ton dialogue de confirmation habituel */}
      <AlerteDialog
        open={showConfirmDialog}
        handleClose={cancelClose}
        deleteProject={confirmClose}
        title="Abandonner ?"
        contentText="Toutes les modifications non enregistrées seront perdues."
        color="error" 
      />
    </Box>
  );
}

export default AddUpdateTurnover;